import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Alert } from './ui/alert';
import {
  Trash2,
  Eye,
  Search,
  Loader2,
  Users,
  RefreshCw,
  ChevronDown,
} from 'lucide-react';
import { adminService } from '../services/admin';
import { ExternalUser } from '../services/auth';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Checkbox } from './ui/checkbox';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<ExternalUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<ExternalUser | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string>('');

  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);

  // Temporary states for the popover
  const [tempSortBy, setTempSortBy] = useState('createdAt');
  const [tempSortOrder, setTempSortOrder] = useState('DESC');
  const [tempStatusFilter, setTempStatusFilter] = useState<string[]>([]);
  const [sortByDropdownOpen, setSortByDropdownOpen] = useState(false);
  const [sortOrderDropdownOpen, setSortOrderDropdownOpen] = useState(false);
  const [popoverKey, setPopoverKey] = useState(0);
  const sortByRef = useRef<HTMLDivElement>(null);
  const sortOrderRef = useRef<HTMLDivElement>(null);

  // Sync temp states with real states
  useEffect(() => {
    setTempSortBy(sortBy);
    setTempSortOrder(sortOrder);
    setTempStatusFilter(statusFilter);
  }, [sortBy, sortOrder, statusFilter]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sortByRef.current &&
        !sortByRef.current.contains(event.target as Node)
      ) {
        setSortByDropdownOpen(false);
      }
      if (
        sortOrderRef.current &&
        !sortOrderRef.current.contains(event.target as Node)
      ) {
        setSortOrderDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchUsers = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setSearchLoading(true);
      }
      const params = {
        sortBy,
        sortOrder,
        status: statusFilter.length > 0 ? statusFilter : undefined,
        search: searchTerm || undefined,
      };
      const data = await adminService.getDashboardUsers(params);
      setUsers(data as ExternalUser[]);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch users');
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setSearchLoading(false);
      }
    }
  };

  // Initial load
  useEffect(() => {
    fetchUsers(true);
  }, []);

  // Subsequent searches and filters
  useEffect(() => {
    // Skip initial load
    if (loading) return;

    const debounceTimer = setTimeout(() => {
      fetchUsers(false);
    }, 300); // Debounce search input

    return () => clearTimeout(debounceTimer);
  }, [sortBy, sortOrder, statusFilter, searchTerm]);

  const handleUpdateUser = async (
    userId: string,
    updates: Partial<ExternalUser>
  ) => {
    try {
      await adminService.updateUser(userId, updates);
      setUsers(
        users.map(user => (user.id === userId ? { ...user, ...updates } : user))
      );
      setSelectedUser(null);
      setShowUserModal(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update user');
    }
  };

  const handleDelete = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await adminService.deleteUser(userId);
        setUsers(users.filter(user => user.id !== userId));
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete user');
      }
    }
  };

  const handleSyncUsers = async () => {
    setSyncing(true);
    setSyncMessage('');
    try {
      await adminService.forceSyncUsers();
      setSyncMessage('User synchronization completed successfully');
      // Refresh the user list after sync
      fetchUsers(false);
    } catch (err: any) {
      setSyncMessage(err.response?.data?.message || 'Failed to sync users');
    } finally {
      setSyncing(false);
      // Clear message after 5 seconds
      setTimeout(() => setSyncMessage(''), 5000);
    }
  };

  const handleStatusFilterChange = (status: string) => {
    setTempStatusFilter(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const handleSortByChange = (value: string) => {
    setTempSortBy(value);
  };

  const handleSortOrderChange = (value: string) => {
    setTempSortOrder(value);
  };

  // Reset temp states when needed
  const resetTempStatesOnOpen = () => {
    setTempSortBy(sortBy);
    setTempSortOrder(sortOrder);
    setTempStatusFilter(statusFilter);
  };

  const handleApplyFilters = () => {
    // Close dropdowns first
    setSortByDropdownOpen(false);
    setSortOrderDropdownOpen(false);

    // Apply filters
    setSortBy(tempSortBy);
    setSortOrder(tempSortOrder);
    setStatusFilter(tempStatusFilter);

    // Force close the popover by changing its key
    setPopoverKey(prev => prev + 1);
  };

  const handleResetFilters = () => {
    setTempSortBy('createdAt');
    setTempSortOrder('DESC');
    setTempStatusFilter([]);
    setSortBy('createdAt');
    setSortOrder('DESC');
    setStatusFilter([]);
    setSortByDropdownOpen(false);
    setSortOrderDropdownOpen(false);

    // Force close the popover by changing its key
    setPopoverKey(prev => prev + 1);
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center py-8'>
        <Loader2 className='w-8 h-8 animate-spin' />
      </div>
    );
  }

  return (
    <div className='space-y-4 sm:space-y-6'>
      <div className='flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4'>
        <div>
          <h1 className='text-2xl sm:text-3xl font-bold text-gray-900'>
            User Management
          </h1>
          <p className='text-sm sm:text-base text-gray-600'>Manage users</p>
        </div>
        {/* <div className='flex items-center gap-3'>
          <button
            onClick={handleSyncUsers}
            disabled={syncing}
            className='flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors'
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Users'}
          </button>
        </div> */}
      </div>

      {error && <Alert variant='destructive'>{error}</Alert>}
      {syncMessage && (
        <Alert
          variant={
            syncMessage.includes('successfully') ? 'default' : 'destructive'
          }
        >
          {syncMessage}
        </Alert>
      )}

      <Card>
        <CardHeader className='p-4 sm:p-6'>
          <CardTitle className='flex items-center gap-2 text-lg sm:text-xl'>
            <Users className='w-4 h-4 sm:w-5 sm:h-5' />
            Users ({users.length})
          </CardTitle>
          <div className='space-y-3'>
            <div className='relative w-full'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400' />
              <Input
                placeholder='Search users...'
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className='pl-10'
              />
            </div>
            <div className='flex justify-start'>
              <Popover key={popoverKey}>
                <PopoverTrigger asChild>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={resetTempStatesOnOpen}
                  >
                    Sort & Filter
                    <ChevronDown className='w-4 h-4 ml-2' />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className='w-64 p-4 space-y-4'>
                  <div className='space-y-2'>
                    <Label>Sort by</Label>
                    <div className='relative' ref={sortByRef}>
                      <Button
                        variant='outline'
                        onClick={() =>
                          setSortByDropdownOpen(!sortByDropdownOpen)
                        }
                        className='w-full justify-between'
                      >
                        {tempSortBy === 'createdAt' && 'Registration Date'}
                        {tempSortBy === 'hasPassword' && 'Status'}
                        {tempSortBy === 'lastAccess' && 'Access Date'}
                        <ChevronDown className='h-4 w-4 opacity-50' />
                      </Button>
                      {sortByDropdownOpen && (
                        <div className='absolute top-full left-0 z-50 w-full mt-1 bg-white border rounded-md shadow-lg'>
                          <div
                            className='px-3 py-2 text-sm cursor-pointer hover:bg-gray-100'
                            onClick={() => {
                              setTempSortBy('createdAt');
                              setSortByDropdownOpen(false);
                            }}
                          >
                            Registration Date
                          </div>
                          <div
                            className='px-3 py-2 text-sm cursor-pointer hover:bg-gray-100'
                            onClick={() => {
                              setTempSortBy('hasPassword');
                              setSortByDropdownOpen(false);
                            }}
                          >
                            Status
                          </div>
                          <div
                            className='px-3 py-2 text-sm cursor-pointer hover:bg-gray-100'
                            onClick={() => {
                              setTempSortBy('lastAccess');
                              setSortByDropdownOpen(false);
                            }}
                          >
                            Access Date
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='space-y-2'>
                    <Label>Sort direction</Label>
                    <div className='relative' ref={sortOrderRef}>
                      <Button
                        variant='outline'
                        onClick={() =>
                          setSortOrderDropdownOpen(!sortOrderDropdownOpen)
                        }
                        className='w-full justify-between'
                      >
                        {tempSortOrder === 'ASC' && 'Ascending'}
                        {tempSortOrder === 'DESC' && 'Descending'}
                        <ChevronDown className='h-4 w-4 opacity-50' />
                      </Button>
                      {sortOrderDropdownOpen && (
                        <div className='absolute top-full left-0 z-50 w-full mt-1 bg-white border rounded-md shadow-lg'>
                          <div
                            className='px-3 py-2 text-sm cursor-pointer hover:bg-gray-100'
                            onClick={() => {
                              setTempSortOrder('ASC');
                              setSortOrderDropdownOpen(false);
                            }}
                          >
                            Ascending
                          </div>
                          <div
                            className='px-3 py-2 text-sm cursor-pointer hover:bg-gray-100'
                            onClick={() => {
                              setTempSortOrder('DESC');
                              setSortOrderDropdownOpen(false);
                            }}
                          >
                            Descending
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='space-y-2'>
                    <Label>Filter by status</Label>
                    <div className='flex items-center space-x-2'>
                      <Checkbox
                        id='registered'
                        checked={tempStatusFilter.includes('registered')}
                        onCheckedChange={() =>
                          handleStatusFilterChange('registered')
                        }
                      />
                      <Label htmlFor='registered'>Registered</Label>
                    </div>
                    <div className='flex items-center space-x-2'>
                      <Checkbox
                        id='pending'
                        checked={tempStatusFilter.includes('pending')}
                        onCheckedChange={() =>
                          handleStatusFilterChange('pending')
                        }
                      />
                      <Label htmlFor='pending'>Pending</Label>
                    </div>
                  </div>
                  <div className='flex space-x-2 pt-3 border-t'>
                    <Button
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleApplyFilters();
                      }}
                      size='sm'
                      className='flex-1'
                      type='button'
                    >
                      Apply
                    </Button>
                    <Button
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleResetFilters();
                      }}
                      variant='outline'
                      size='sm'
                      className='flex-1'
                    >
                      Reset
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardHeader>
        <CardContent className='p-4 sm:p-6 space-y-3 sm:space-y-4'>
          {searchLoading ? (
            <div className='flex items-center justify-center py-8'>
              <Loader2 className='w-6 h-6 animate-spin text-gray-400' />
              <span className='ml-2 text-gray-500'>Loading users...</span>
            </div>
          ) : (
            <>
              {users.map(user => (
                <div
                  key={user.id}
                  className='flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 border rounded-lg gap-3 sm:gap-4'
                >
                  <div className='flex-1 space-y-1 sm:space-y-0'>
                    <div className='flex flex-col sm:flex-row sm:items-center gap-2'>
                      <span className='font-medium text-gray-900'>
                        {user.firstName || ''} {user.lastName || ''}
                      </span>
                      <div className='flex gap-2'>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            user.hasPassword
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {user.hasPassword ? 'Registered' : 'Pending'}
                        </span>
                      </div>
                    </div>
                    <p className='text-sm text-gray-600'>{user.email}</p>
                    {user.phone && (
                      <p className='text-sm text-gray-600'>
                        Phone: {user.phone}
                      </p>
                    )}
                    {user.company && (
                      <p className='text-sm text-gray-600'>
                        Company: {user.company}
                      </p>
                    )}
                    {user.primaryRole && (
                      <p className='text-sm text-gray-600'>
                        Role: {user.primaryRole}
                      </p>
                    )}
                    {user.lastAccess && (
                      <p className='text-sm text-gray-500'>
                        Last Access:{' '}
                        {new Date(user.lastAccess).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className='flex gap-2'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => {
                        setSelectedUser(user);
                        setShowUserModal(true);
                      }}
                      className='flex-1 sm:flex-none'
                    >
                      <Eye className='w-4 h-4 mr-1' />
                      View
                    </Button>
                    <Button
                      variant='destructive'
                      size='sm'
                      onClick={() => handleDelete(user.id)}
                      className='flex-1 sm:flex-none'
                    >
                      <Trash2 className='w-4 h-4 mr-1' />
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
              {users.length === 0 && (
                <div className='text-center py-8 text-gray-500'>
                  {searchTerm
                    ? 'No users found matching your search.'
                    : 'No users found.'}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto p-6'>
            <div className='flex justify-between items-center mb-4 sm:mb-6'>
              <h2 className='text-xl sm:text-2xl font-bold text-gray-900'>
                User Details
              </h2>
              <Button
                variant='ghost'
                size='sm'
                onClick={() => setShowUserModal(false)}
              >
                ✕
              </Button>
            </div>
            <div className='space-y-4'>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    First Name
                  </label>
                  <Input
                    value={selectedUser.firstName || ''}
                    onChange={e =>
                      setSelectedUser({
                        ...selectedUser,
                        firstName: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Last Name
                  </label>
                  <Input
                    value={selectedUser.lastName || ''}
                    onChange={e =>
                      setSelectedUser({
                        ...selectedUser,
                        lastName: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Email
                </label>
                <Input
                  type='email'
                  value={selectedUser.email}
                  onChange={e =>
                    setSelectedUser({ ...selectedUser, email: e.target.value })
                  }
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  LinkedIn
                </label>
                <Input
                  value={selectedUser.linkedin || ''}
                  onChange={e =>
                    setSelectedUser({
                      ...selectedUser,
                      linkedin: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Company
                </label>
                <Input
                  value={selectedUser.company || ''}
                  onChange={e =>
                    setSelectedUser({
                      ...selectedUser,
                      company: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Primary Role
                </label>
                <Input
                  value={selectedUser.primaryRole || ''}
                  onChange={e =>
                    setSelectedUser({
                      ...selectedUser,
                      primaryRole: e.target.value,
                    })
                  }
                />
              </div>
              <div className='flex justify-end gap-2 pt-4'>
                <Button
                  variant='outline'
                  onClick={() => setShowUserModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    const updates: Partial<ExternalUser> = {
                      firstName: selectedUser.firstName,
                      lastName: selectedUser.lastName,
                      email: selectedUser.email,
                      linkedin: selectedUser.linkedin,
                      company: selectedUser.company,
                      primaryRole: selectedUser.primaryRole,
                    };

                    handleUpdateUser(selectedUser.id, updates);
                  }}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
